// src/pages/SupportTicketsPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { FiMessageSquare, FiClock, FiCheckCircle } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import styles from './SupportTicketsPage.module.css';
import { SupportService, Ticket, TicketPriority, TicketStatus } from '../services/support.service';
import { SystemService } from '../services/system.service';

export default function SupportTicketsPage() {
  const { t } = useTranslation();

  const [enabled, setEnabled] = useState(true);
  const [stats, setStats] = useState({ open: 0, inProgress: 0, resolved: 0 });
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);

  const [savingId, setSavingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const priorityMap: Record<TicketPriority, { label: string; className: string }> = useMemo(
    () => ({
      high: { label: t('admin.priority_alta'), className: styles.priorityHigh },
      medium: { label: t('admin.priority_media'), className: styles.priorityMedium },
      low: { label: t('admin.priority_baja'), className: styles.priorityLow }
    }),
    [t]
  );

  const statusMap: Record<TicketStatus, { label: string; className: string }> = useMemo(
    () => ({
      open: { label: t('admin.status_open'), className: styles.statusOpen },
      in_progress: { label: t('admin.status_inprogress'), className: styles.statusInProgress },
      closed: { label: t('admin.status_closed'), className: styles.statusClosed }
    }),
    [t]
  );

  const load = async () => {
    setLoading(true);
    setError(null);

    try {
      const flag = await SystemService.getFlag('support_enabled');
      setEnabled(!!flag);
    } catch {
      setEnabled(true);
    }

    try {
      const [s, list] = await Promise.all([SupportService.getStats(), SupportService.getRecentTickets(20)]);
      setStats({ open: s?.open ?? 0, inProgress: s?.inProgress ?? 0, resolved: s?.resolved ?? 0 });
      setTickets(Array.isArray(list) ? list : []);
    } catch {
      setStats({ open: 0, inProgress: 0, resolved: 0 });
      setTickets([]);
      setError(t('admin.support_load_error') || 'No se pudo cargar la información.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cycleStatus = (s: TicketStatus): TicketStatus => {
    if (s === 'open') return 'in_progress';
    if (s === 'in_progress') return 'closed';
    return 'open';
  };

  const onAdvanceStatus = async (ticket: Ticket) => {
    const next = cycleStatus(ticket.status);
    setSavingId(ticket.id);
    setError(null);

    const prevTickets = tickets;
    setTickets(curr => curr.map(tk => (tk.id === ticket.id ? { ...tk, status: next } : tk)));

    try {
      await SupportService.updateTicketStatus(ticket.id, { status: next });
      await load();
    } catch {
      setTickets(prevTickets);
      setError(t('admin.support_save_error') || 'No se pudo guardar el cambio.');
    } finally {
      setSavingId(null);
    }
  };

  const content = () => {
    if (loading) {
      return (
        <>
          <div className={styles.statsGrid}>
            <div className={`${styles.statCard} ${styles.skeleton}`}>
              <div className={`${styles.iconBox} ${styles.iconOpen}`} aria-hidden="true" />
              <div className={styles.statText}>
                <div className={`${styles.skeletonLine} ${styles.skeletonNum}`} />
                <div className={`${styles.skeletonLine} ${styles.skeletonLabel}`} />
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.skeleton}`}>
              <div className={`${styles.iconBox} ${styles.iconProgress}`} aria-hidden="true" />
              <div className={styles.statText}>
                <div className={`${styles.skeletonLine} ${styles.skeletonNum}`} />
                <div className={`${styles.skeletonLine} ${styles.skeletonLabel}`} />
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.skeleton}`}>
              <div className={`${styles.iconBox} ${styles.iconClosed}`} aria-hidden="true" />
              <div className={styles.statText}>
                <div className={`${styles.skeletonLine} ${styles.skeletonNum}`} />
                <div className={`${styles.skeletonLine} ${styles.skeletonLabel}`} />
              </div>
            </div>
          </div>

          <section className={styles.ticketSection} aria-busy="true" aria-live="polite">
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{t('admin.recent_tickets')}</h2>
            </div>

            <div className={styles.ticketList}>
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className={`${styles.ticketCard} ${styles.skeleton}`}>
                  <div className={styles.ticketBody}>
                    <div className={styles.ticketHeader}>
                      <div className={`${styles.skeletonLine} ${styles.skeletonId}`} />
                      <div className={`${styles.skeletonLine} ${styles.skeletonSubject}`} />
                    </div>
                    <div className={`${styles.skeletonLine} ${styles.skeletonUser}`} />
                  </div>
                  <div className={styles.ticketMeta}>
                    <div className={`${styles.chip} ${styles.skeletonChip}`} />
                    <div className={`${styles.chipButton} ${styles.skeletonChip}`} />
                    <div className={`${styles.actionButton} ${styles.skeletonButton}`} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      );
    }

    if (!enabled) {
      return (
        <div className={styles.noticeCard} role="status">
          {t('admin.support_disabled') || 'El módulo de soporte no está disponible por el momento.'}
        </div>
      );
    }

    return (
      <>
        {error ? (
          <div className={styles.errorBanner} role="alert">
            <div className={styles.errorTitle}>{t('admin.support_error_title') || 'No se pudo cargar la información'}</div>
            <div className={styles.errorText}>{error}</div>
          </div>
        ) : null}

        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={`${styles.iconBox} ${styles.iconOpen}`}>
              <FiMessageSquare />
            </div>
            <div className={styles.statText}>
              <div className={styles.statValue}>{stats.open}</div>
              <div className={styles.statLabel}>{t('admin.tickets_open')}</div>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={`${styles.iconBox} ${styles.iconProgress}`}>
              <FiClock />
            </div>
            <div className={styles.statText}>
              <div className={styles.statValue}>{stats.inProgress}</div>
              <div className={styles.statLabel}>{t('admin.tickets_in_progress')}</div>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={`${styles.iconBox} ${styles.iconClosed}`}>
              <FiCheckCircle />
            </div>
            <div className={styles.statText}>
              <div className={styles.statValue}>{stats.resolved}</div>
              <div className={styles.statLabel}>{t('admin.tickets_resolved')}</div>
            </div>
          </div>
        </div>

        <section className={styles.ticketSection}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('admin.recent_tickets')}</h2>
          </div>

          <div className={styles.ticketList}>
            {tickets.map(ticket => (
              <div key={ticket.id} className={styles.ticketCard}>
                <div className={styles.ticketBody}>
                  <div className={styles.ticketHeader}>
                    <span className={styles.ticketId}>#{ticket.id}</span>
                    <span className={styles.ticketSubject}>{ticket.subject}</span>
                  </div>
                  <p className={styles.ticketUser}>
                    {t('admin.user')}: {ticket.user} • {ticket.date}
                  </p>
                </div>

                <div className={styles.ticketMeta}>
                  <span className={`${styles.chip} ${priorityMap[ticket.priority].className}`}>
                    {priorityMap[ticket.priority].label}
                  </span>

                  <button
                    type="button"
                    className={`${styles.chipButton} ${statusMap[ticket.status].className}`}
                    onClick={() => onAdvanceStatus(ticket)}
                    disabled={savingId === ticket.id}
                    title={t('admin.click_to_advance_status') || 'Click para avanzar estatus'}
                  >
                    {savingId === ticket.id ? (t('admin.saving') || 'Guardando...') : statusMap[ticket.status].label}
                  </button>

                  <button
                    type="button"
                    className={styles.actionButton}
                    disabled
                    title={t('admin.reply_soon') || 'Función en desarrollo'}
                  >
                    {t('admin.reply')}
                  </button>
                </div>
              </div>
            ))}

            {tickets.length === 0 ? (
              <div className={styles.noticeCard} role="status">
                {t('admin.no_tickets') || 'No hay tickets recientes.'}
              </div>
            ) : null}
          </div>
        </section>
      </>
    );
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h1 className={styles.title}>{t('admin.support_title')}</h1>
          <p className={styles.subtitle}>{t('admin.support_subtitle')}</p>
        </div>
      </header>
      {content()}
    </div>
  );
}
